import { useState, useEffect, useCallback, Fragment } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { RefreshCw, ChevronDown, ChevronUp, Ban, Hash } from "lucide-react";
import { toast } from "sonner";

const statusClass = {
  posted: "badge-posted", pending: "badge-pending", failed: "badge-failed",
  voided: "badge-voided", void_failed: "badge-void-failed", local_only: "badge-local-only"
};

export default function Transactions() {
  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: "", room: "", date_from: "", date_to: "" });
  const [expanded, setExpanded] = useState(null);
  const [voidModal, setVoidModal] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [confModal, setConfModal] = useState(false);
  const [confTarget, setConfTarget] = useState(null);
  const [confNumber, setConfNumber] = useState("");

  const fetchTxns = useCallback(async () => {
    const params = { page, per_page: 30, ...filters };
    Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
    const res = await api.get("/admin/transactions", { params });
    setTxns(res.data.transactions);
    setTotal(res.data.total);
  }, [page, filters]);

  useEffect(() => { fetchTxns(); }, [fetchTxns]);

  const openVoid = (txn) => {
    setVoidTarget(txn); setVoidReason(""); setVoidModal(true);
  };

  const submitVoid = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/admin/transactions/${voidTarget.id}/void`, { reason: voidReason });
      if (res.data.status === "voided") {
        toast.success("Transaction voided successfully");
      } else {
        toast.error("Void failed - please retry");
      }
      setVoidModal(false); fetchTxns();
    } catch (err) { toast.error(err.response?.data?.detail || "Void failed"); }
    finally { setLoading(false); }
  };

  const retryTxn = async (txn) => {
    try {
      const res = await api.post(`/admin/transactions/${txn.id}/retry`);
      toast.success(`Transaction status: ${res.data.status}`);
      fetchTxns();
    } catch (err) { toast.error("Retry failed"); }
  };

  const openConfModal = (txn) => {
    setConfTarget(txn);
    setConfNumber("");
    setConfModal(true);
  };

  const submitConfRetry = async () => {
    if (!confNumber.trim()) return;
    setLoading(true);
    try {
      const res = await api.post(`/admin/transactions/${confTarget.id}/retry-with-confirmation`, {
        confirmation_number: confNumber.trim(),
      });
      if (res.data.status === "posted") {
        toast.success(`Posted to ${res.data.guest_name || 'guest'} (Res: ${res.data.reservation_id})`);
      } else {
        toast.error(res.data.error || "Posting failed — check PMS error");
      }
      setConfModal(false);
      fetchTxns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post with confirmation number");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Transactions</h1>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white border border-gray-200 p-4">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger className="w-36 rounded-none h-9 text-sm mt-1" data-testid="filter-status">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="local_only">Local Only</SelectItem>
              <SelectItem value="voided">Voided</SelectItem>
              <SelectItem value="void_failed">Void Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">Room</Label>
          <Input value={filters.room} onChange={(e) => setFilters({ ...filters, room: e.target.value })}
            className="w-24 rounded-none h-9 text-sm mt-1" placeholder="Room" data-testid="filter-room" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">From</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="rounded-none h-9 text-sm mt-1" data-testid="filter-date-from" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">To</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="rounded-none h-9 text-sm mt-1" data-testid="filter-date-to" />
        </div>
        <Button onClick={() => { setPage(1); fetchTxns(); }} className="rounded-none bg-black text-white h-9 text-xs" data-testid="apply-filters">
          Apply
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200">
        <table className="w-full table-dense">
          <thead><tr className="border-b border-gray-100">
            <th className="w-8"></th><th className="text-left">Ref</th><th className="text-left">Room</th>
            <th className="text-left">Guest</th><th className="text-left">Operator</th>
            <th className="text-right">Total</th><th className="text-center">Status</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {txns.map((txn) => (
              <Fragment key={txn.id}>
                <tr key={txn.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setExpanded(expanded === txn.id ? null : txn.id)}>
                  <td>{expanded === txn.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</td>
                  <td className="font-mono text-xs">{txn.transaction_ref}</td>
                  <td className="font-semibold">{txn.room_number}</td>
                  <td className="text-gray-600 text-sm">{txn.guest_name}</td>
                  <td className="text-gray-500 text-sm">{txn.operator_name}</td>
                  <td className="text-right font-mono font-medium">${txn.grand_total?.toFixed(2)}</td>
                  <td className="text-center">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${statusClass[txn.status] || ''}`}>
                      {txn.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    {['posted', 'local_only', 'failed', 'pending'].includes(txn.status) && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700"
                        onClick={() => openVoid(txn)} data-testid={`void-txn-${txn.id}`}>
                        <Ban className="w-3 h-3 mr-1" /> Void
                      </Button>
                    )}
                    {(txn.status === 'failed' || txn.status === 'void_failed' || txn.status === 'local_only') && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-500"
                        onClick={() => retryTxn(txn)} data-testid={`retry-txn-${txn.id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Retry
                      </Button>
                    )}
                    {(txn.status === 'failed' || txn.status === 'local_only') && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-500"
                        onClick={() => openConfModal(txn)} data-testid={`conf-retry-txn-${txn.id}`}>
                        <Hash className="w-3 h-3 mr-1" /> Conf #
                      </Button>
                    )}
                  </td>
                </tr>
                {expanded === txn.id && (
                  <tr key={`${txn.id}-detail`} className="bg-gray-50">
                    <td colSpan={8} className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs mb-3">
                        <div><span className="text-gray-400 uppercase tracking-wider">Time</span><br />{new Date(txn.created_at).toLocaleString()}</div>
                        <div><span className="text-gray-400 uppercase tracking-wider">Device</span><br />{txn.device_summary || '—'}</div>
                        <div><span className="text-gray-400 uppercase tracking-wider">Device ID</span><br />{txn.device_cookie_id?.slice(0, 8) || '—'}</div>
                        <div><span className="text-gray-400 uppercase tracking-wider">Reservation</span><br />{txn.reservation_id}</div>
                      </div>
                      <table className="w-full text-xs">
                        <thead><tr className="border-b border-gray-200">
                          <th className="text-left py-1">Item</th><th className="text-left">Code</th><th className="text-right">Qty</th>
                          <th className="text-right">Price</th><th className="text-right">Total</th><th className="text-left">Taxes</th>
                        </tr></thead>
                        <tbody>
                          {txn.lines?.map((line, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              <td className="py-1">{line.item_name}</td>
                              <td className="font-mono">{line.item_code}</td>
                              <td className="text-right">{line.quantity}</td>
                              <td className="text-right font-mono">${line.unit_price?.toFixed(2)}</td>
                              <td className="text-right font-mono">${line.line_total?.toFixed(2)}</td>
                              <td>{line.tax_detail?.map((t, j) => (
                                <span key={j} className="text-gray-400">{t.tax_name}: ${t.tax_amount?.toFixed(2)} </span>
                              ))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="flex justify-end gap-6 mt-2 text-xs font-medium">
                        <span>Subtotal: ${txn.subtotal?.toFixed(2)}</span>
                        <span>Tax: ${txn.tax_total?.toFixed(2)}</span>
                        {txn.gratuity > 0 && <span>Svc Charge: ${txn.gratuity?.toFixed(2)}</span>}
                        <span className="font-bold">Total: ${txn.grand_total?.toFixed(2)}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {txns.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-gray-400">No transactions found</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 30 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">{total} total transactions</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-none h-7 text-xs">Prev</Button>
            <Button variant="outline" size="sm" disabled={page * 30 >= total} onClick={() => setPage(p => p + 1)} className="rounded-none h-7 text-xs">Next</Button>
          </div>
        </div>
      )}

      {/* Void Modal */}
      <Dialog open={voidModal} onOpenChange={setVoidModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-red-600">Void Transaction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Voiding <strong>{voidTarget?.transaction_ref}</strong> for room <strong>{voidTarget?.room_number}</strong> (${voidTarget?.grand_total?.toFixed(2)})</p>
            {voidTarget?.status === 'posted' && (
              <p className="text-xs text-red-500 bg-red-50 p-2 border border-red-200">A reversal will be sent to the PMS to credit this charge back to the guest.</p>
            )}
            {voidTarget?.status !== 'posted' && (
              <p className="text-xs text-gray-500 bg-gray-50 p-2 border border-gray-200">This transaction was not posted to the PMS. It will be voided locally only.</p>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Reason (required)</Label>
              <Textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                className="mt-1 rounded-none" rows={3} data-testid="void-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidModal(false)} className="rounded-none">Cancel</Button>
            <Button onClick={submitVoid} disabled={voidReason.length < 3 || loading}
              className="rounded-none bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-void-button">
              {loading ? "Processing..." : "Confirm Void"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Number Retry Modal */}
      <Dialog open={confModal} onOpenChange={setConfModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-blue-600">Post by Confirmation #</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Post <strong>{confTarget?.transaction_ref}</strong> (${confTarget?.grand_total?.toFixed(2)}) to a reservation using its Opera confirmation number.</p>
            <p className="text-xs text-blue-500 bg-blue-50 p-2 border border-blue-200">
              Use this when the original room is no longer available (e.g. guest checked out). The charge will be posted to the reservation found by this confirmation number.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Opera Confirmation Number</Label>
              <Input value={confNumber} onChange={(e) => setConfNumber(e.target.value)}
                placeholder="e.g. 487264522" className="mt-1 rounded-none font-mono text-lg tracking-wider"
                data-testid="conf-number-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfModal(false)} className="rounded-none">Cancel</Button>
            <Button onClick={submitConfRetry} disabled={!confNumber.trim() || loading}
              className="rounded-none bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-conf-retry-button">
              {loading ? "Posting..." : "Look Up & Post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
